import { ActionFetch, createCacheKey, fetchResource, handleErrorResponse } from 'hammock';
import moment from 'moment';
import { UseQueryOptions } from 'react-query';

import {
  AnalyticQuery,
  DehydratedAnalyticsQuery,
  QueryMetadata,
  TimeInterval,
} from '@candu-ai/analytics/types';

export const toSecondTimeStamp = (date?: Date): string =>
  Math.round((date?.getTime() ?? 0) / 1000).toString();

const SECONDS_IN_A_DAY = 86400;

export const toEndOfDayTimestamp = (date?: Date): string =>
  (Math.round((date?.getTime() ?? 0) / 1000) + SECONDS_IN_A_DAY - 1).toString();

export const hydrateAnalyticsData = <T>(
  data?: DehydratedAnalyticsQuery,
): AnalyticQuery<T> | undefined => {
  if (!data) {
    return data;
  }

  const { fields } = data;

  const fieldNames = fields.map(({ name }) => name);
  const rows = (data.rows.map((row) =>
    Object.fromEntries(row.map((value, index) => [fieldNames[index], value])),
  ) as unknown) as T[];
  return {
    ...data,
    rows,
  };
};

export const createUseQuery = <T>(
  payload: ActionFetch,
  options?: UseQueryOptions,
  clearCache?: boolean,
) =>
  ({
    queryKey: createCacheKey(payload),
    queryFn: () =>
      fetchResource<DehydratedAnalyticsQuery>({
        ...payload,
        endpoint: payload.endpoint.includes('?')
          ? `${payload.endpoint}${clearCache ? `&cb=true` : ''}`
          : `${payload.endpoint}${clearCache ? `?cb=true` : ''}`,
      })
        .then((data) => hydrateAnalyticsData<T>(data))
        .catch((e) => handleErrorResponse(e, payload)),
    keepPreviousData: true,
    ...(options || {}),
  } as UseQueryOptions<AnalyticQuery<T>>);

export const getTimeIntervalQuery = (searchParams: URLSearchParams) => {
  return searchParams.has('ti') ? `?ti=${searchParams.get('ti')}` : '';
};

export const createMinMaxDates = (
  timeInterval: TimeInterval,
  upperTimeInterval?: TimeInterval,
): [Date, Date] => {
  const now = () => moment().startOf('d');

  const startingPoint = now().subtract(
    timeInterval.duration as number,
    timeInterval.period as 'M' | 'd',
  );
  const endingPoint = upperTimeInterval
    ? now().subtract(upperTimeInterval.duration as number, upperTimeInterval.period as 'M' | 'd')
    : now();

  return [startingPoint.toDate(), endingPoint.toDate()];
};

export const createArrayOfDate = (time: QueryMetadata['time']) => {
  const now = () => moment().utc().startOf('d');

  let startingPoint;
  let endingPoint;
  if (time.timestamp) {
    startingPoint = moment(time.timestamp, `X`).utc().startOf('d');
    endingPoint = time.upperTimestamp ? moment(time.upperTimestamp, `X`).utc().startOf('d') : now();
  } else {
    startingPoint = now()
      .utc()
      .startOf('d')
      .add(1, 'day')
      .subtract(time.timeInterval?.duration as number, time?.timeInterval?.period as 'M' | 'd');

    endingPoint = time.upperTimeInterval
      ? now().subtract(
          time.upperTimeInterval.duration as number,
          time.upperTimeInterval.period as 'M' | 'd',
        )
      : now();
  }

  const dateArray = [];

  while (startingPoint.isSameOrBefore(endingPoint)) {
    dateArray.push(startingPoint.valueOf());
    startingPoint.add(1, 'd');
  }

  return dateArray;
};

export const fillEmptyRecords = <T extends { [e: string]: unknown; date: string }>(
  data: AnalyticQuery<T>,
) => {
  const now = () => moment().utc().startOf('d');
  const { time } = data.metadata;

  let startingPoint;
  let endingPoint;
  if (time.timestamp) {
    startingPoint = moment(time.timestamp, `X`).utc().startOf('d');
    endingPoint = time.upperTimestamp ? moment(time.upperTimestamp, `X`).utc().startOf('d') : now();
  } else {
    startingPoint = now()
      .utc()
      .startOf('d')
      .subtract(time.timeInterval?.duration as number, time?.timeInterval?.period as 'M' | 'd');

    endingPoint = time.upperTimeInterval
      ? now().subtract(
          time.upperTimeInterval.duration as number,
          time.upperTimeInterval.period as 'M' | 'd',
        )
      : now();
  }

  const rowArray: T[] = [];
  const keys = data.fields.map((field) => field.name);
  let i = 0;
  while (startingPoint.isSameOrBefore(endingPoint)) {
    if (data.rows[i]?.date && moment(data.rows[i]?.date).isSame(startingPoint, 'day')) {
      rowArray.push(data.rows[i]);
      i += 1;
    } else {
      rowArray.push({
        ...Object.fromEntries(keys.map((key) => [key, 0])),
        date: startingPoint.valueOf() as unknown,
      } as T);
    }
    startingPoint.add(1, 'd');
  }

  return {
    ...data,
    rows: rowArray,
  };
};
